import React, { useState, useEffect, useRef } from 'react';
import { Search, Plus, Eye, Edit, Users, Power, Settings, ChevronDown, FileDown, RefreshCw, X, Merge } from 'lucide-react';
import Avatar from '../../../../components/common/Avatar';
import DataTable from '../../../../components/common/DataTable';
import LoadingSpinner from '../../../../components/common/LoadingSpinner';
import { LayoutGrid, List } from 'lucide-react';
import CustomerCard from './CustomerCard';
import { getAllCustomers, createCustomer, updateCustomer, toggleCustomerStatus, handleApiError, bulkImportCustomers, bulkUpdateCustomers } from '../../../../services/api/customerApi';
import { getCustomerCategories, getSalesPersons, getCountries, getStatesByCountry } from '../../../../services/api/lookupApi';
import { getCurrentTenant } from '../../../../services/api/companyApi';
import { handleExportExcel as exportXlsx, handleDownloadTemplate as downloadTemplateXlsx, parseImportFile } from './ExcelUtils';
import { useAuth } from '../../../../context/AuthContext';

// Customer modals
import CustomerViewModal from './CustomerViewModal';
import CustomerEditModal from './CustomerEditModal';
import MergeCustomerModal from './MergeCustomerModal';

const CustomersView = ({ role }) => {
    const { user } = useAuth();
    const isSalesPerson = role === 'SALES_PERSON';
    const [statusTab, setStatusTab] = useState('ACTIVE');
    const [activeTab, setActiveTab] = useState('assigned');
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [viewCustomer, setViewCustomer] = useState(null);
    const [editCustomer, setEditCustomer] = useState(null);
    const [showOptionsDropdown, setShowOptionsDropdown] = useState(false);
    const [viewMode, setViewMode] = useState('grid');

    // Merge modal states
    const [showMergeModal, setShowMergeModal] = useState(false);
    const [mergeTarget, setMergeTarget] = useState(null);
    const [mergeDuplicates, setMergeDuplicates] = useState([]);

    // Bulk selection states
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [showBulkReassignModal, setShowBulkReassignModal] = useState(false);
    const [bulkPrimarySp, setBulkPrimarySp] = useState('');
    const [bulkDelegatedSp, setBulkDelegatedSp] = useState('');
    const [bulkCategory, setBulkCategory] = useState('');

    useEffect(() => {
        if (typeof window !== 'undefined') {
            setViewMode(window.innerWidth >= 768 ? 'table' : 'grid');
        }
    }, []);
    
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalElements, setTotalElements] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [sortField, setSortField] = useState('createdAt');
    const [sortDirection, setSortDirection] = useState('desc');
    const [searchTerm, setSearchTerm] = useState('');

    const [importing, setImporting] = useState(false);
    const [importResult, setImportResult] = useState(null);
    const [lookupData, setLookupData] = useState({ categories: [], salesPersons: [], countries: [], states: [] });
    const [companyName, setCompanyName] = useState('all');

    const isFetchingRef = useRef(false);
    const fileInputRef = useRef(null);
    const dropdownRef = useRef(null);

    useEffect(() => {
        loadLookupData();
        loadCompanyInfo();
    }, []);

    useEffect(() => {
        const delay = setTimeout(() => {
            loadCustomers();
        }, 300);
        return () => clearTimeout(delay);
    }, [statusTab, activeTab, currentPage, pageSize, searchTerm, sortField, sortDirection]);

    // Reset selection on pagination or filter changes
    useEffect(() => {
        setSelectedIds(new Set());
    }, [statusTab, activeTab, currentPage, pageSize, searchTerm]);

    const handleToggleSelect = (customerId) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(customerId)) {
                next.delete(customerId);
            } else {
                next.add(customerId);
            }
            return next;
        });
    };

    const handleSelectAll = () => {
        const pageIds = displayCustomers.map(c => c.id);
        const allSelectedOnPage = pageIds.length > 0 && pageIds.every(id => selectedIds.has(id));

        setSelectedIds(prev => {
            const next = new Set(prev);
            if (allSelectedOnPage) {
                pageIds.forEach(id => next.delete(id));
            } else {
                pageIds.forEach(id => next.add(id));
            }
            return next;
        });
    };

    const handleBulkReassignSubmit = async () => {
        if (selectedIds.size === 0) return;
        setLoading(true);
        try {
            await bulkUpdateCustomers({
                customerPublicIds: Array.from(selectedIds),
                categoryPublicId: bulkCategory || null,
                salesPersonPublicId: bulkPrimarySp || null,
                delegatedSalesPersonPublicId: bulkDelegatedSp || null
            });
            setSelectedIds(new Set());
            setShowBulkReassignModal(false);
            setBulkPrimarySp('');
            setBulkDelegatedSp('');
            setBulkCategory('');
            await loadCustomers();
        } catch (err) {
            alert('Bulk update failed: ' + handleApiError(err));
        } finally {
            setLoading(false);
        }
    };

    // Close dropdown on click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowOptionsDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const loadLookupData = async () => {
        try {
            const cats = await getCustomerCategories();
            const users = await getSalesPersons();
            const nations = await getCountries();
            let fetchedStates = [];
            const uae = nations.find(c => c.name === 'United Arab Emirates');
            if (uae) {
                fetchedStates = await getStatesByCountry(uae.publicId);
            }
            setLookupData({
                categories: cats,
                salesPersons: users,
                countries: nations,
                states: fetchedStates
            });
        } catch (err) { console.error('Error loading lookup data:', err); }
    };

    const loadCompanyInfo = async () => {
        try {
            const info = await getCurrentTenant();
            if (info && info.businessName) {
                setCompanyName(info.businessName.replace(/\s+/g, '_').toLowerCase());
            }
        } catch (err) { console.error('Error loading company info:', err); }
    };

    const loadCustomers = async () => {
        if (isFetchingRef.current) return;
        isFetchingRef.current = true;
        try {
            setLoading(true);
            const params = {
                page: currentPage - 1,
                size: pageSize,
                search: searchTerm,
                status: statusTab,
                sort: `${sortField},${sortDirection}`
            };
            
            if (isSalesPerson) {
                params.salesPersonId = user?.publicId || user?.id;
                params.salesPersonRole = activeTab;
            }

            const data = await getAllCustomers(params);
            setCustomers(data.content || []);
            setTotalElements(data.totalElements || 0);
            setTotalPages(data.totalPages || 0);
        } catch (err) { 
            setError(handleApiError(err)); 
        }
        finally { setLoading(false); isFetchingRef.current = false; }
    };

    const handleExportExcel = async (dataToExport, isSelection = false) => {
        if (!dataToExport || dataToExport.length === 0) {
            return handleExportAll();
        }
        try {
            await exportXlsx(dataToExport, companyName, isSelection);
            setShowOptionsDropdown(false);
        } catch (err) { alert('Export failed'); }
    };

    const handleExportAll = async () => {
        try {
            setLoading(true);
            const exportSize = totalElements > 0 ? totalElements : 1000;
            const params = {
                page: 0,
                size: exportSize,
                search: searchTerm,
                status: statusTab || 'ACTIVE',
                sort: `${sortField},${sortDirection}`
            };
            
            if (isSalesPerson) {
                params.salesPersonId = user?.publicId || user?.id;
                params.salesPersonRole = activeTab;
            }

            const data = await getAllCustomers(params);
            const allRecords = data.content || [];
            
            if (allRecords.length === 0) {
                alert('No records found to export');
                return;
            }

            await exportXlsx(allRecords, companyName, false);
            setShowOptionsDropdown(false);
        } catch (err) { 
            alert('Export failed: ' + handleApiError(err)); 
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadTemplate = async () => {
        try {
            await downloadTemplateXlsx(lookupData);
            setShowOptionsDropdown(false);
        } catch (err) { alert('Template download failed'); }
    };

    const handleImportExcel = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        try {
            setImporting(true);
            const customersToImport = await parseImportFile(file, lookupData);
            const result = await bulkImportCustomers(customersToImport);
            setImportResult(result);
            await loadCustomers();
        } catch (err) { alert('Import failed: ' + err.message); }
        finally {
            setImporting(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleSaveCustomer = async (formData) => {
        try {
            if (formData.id) {
                // Update existing customer
                await updateCustomer(formData.id, formData);
            } else {
                // Create new customer
                await createCustomer(formData);
            }
            await loadCustomers();
        } catch (err) {
            throw err; // re-throw so CustomerEditModal knows it failed and shows premium inline error
        }
    };

    const handleToggleStatus = async (customer) => {
        try {
            const newActive = !customer.active;
            await toggleCustomerStatus(customer.id, newActive);
            await loadCustomers();
        } catch (err) {
            alert(handleApiError(err));
        }
    };

    const displayCustomers = customers;

    const columns = [
        { header: (
            <input 
                type="checkbox"
                checked={displayCustomers.length > 0 && displayCustomers.every(c => selectedIds.has(c.id))}
                onChange={handleSelectAll}
                className="w-3.5 h-3.5 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer"
            />
        ), field: 'checkbox' },
        { header: 'Name', field: 'name' },
        { header: 'Category', field: 'categoryName' },
        { header: 'TRN', field: 'trn' },
        { header: 'Contact', field: 'mobileNumber' },
        { header: 'Location', field: 'address' },
        { header: 'Assigned SP', field: 'assignedSalesPersons' },
        { header: 'Delegated SP', field: 'delegatedSalesPersons' },
        { header: 'ACTIONS', field: null }
    ];

    const renderRow = (customer) => (
        <tr key={customer.id} className={`hover:bg-blue-50/30 transition-colors group ${!customer.active ? 'opacity-60 grayscale-[0.5]' : ''} ${selectedIds.has(customer.id) ? 'bg-blue-50/10' : ''}`}>
            <td className="px-3 py-2 w-12">
                <input
                    type="checkbox"
                    checked={selectedIds.has(customer.id)}
                    onChange={() => handleToggleSelect(customer.id)}
                    className="w-3.5 h-3.5 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer"
                />
            </td>
            <td className="px-3 py-2">
                <div className="flex items-center gap-2">
                    <Avatar name={customer.name} size="sm" shape="circle" />
                    <div>
                        <div className="font-semibold text-[11px] text-gray-900">{customer.name}</div>
                        {!customer.active && <span className="text-[9px] px-1 bg-gray-200 text-gray-600 rounded">Inactive</span>}
                    </div>
                </div>
            </td>
            <td className="px-3 py-2 text-[10px] whitespace-nowrap">{customer.categoryName || 'N/A'}</td>
            <td className="px-3 py-2 text-[10px] font-mono whitespace-nowrap">{customer.trn || <span className="text-gray-300">—</span>}</td>
            <td className="px-3 py-2 whitespace-nowrap">
                <div className="text-[11px] font-semibold text-gray-900">{customer.mobileNumber || 'N/A'}</div>
                {customer.whatsappNumber && customer.whatsappNumber !== customer.mobileNumber && (
                    <div className="text-[10px] text-green-600 font-medium mt-0.5">
                        WA: {customer.whatsappNumber}
                    </div>
                )}
            </td>
            <td className="px-3 py-2 text-[11px]">{customer.address || 'N/A'}</td>
            <td className="px-3 py-2 text-[10px] whitespace-nowrap">
                {customer.assignedSalesPersons?.length
                    ? customer.assignedSalesPersons.map(sp => sp.fullName).join(', ')
                    : <span className="text-gray-400">—</span>}
            </td>
            <td className="px-3 py-2 text-[10px] whitespace-nowrap">
                {customer.delegatedSalesPersons?.length
                    ? customer.delegatedSalesPersons.map(sp => sp.fullName).join(', ')
                    : <span className="text-gray-400">—</span>}
            </td>
            <td className="px-3 py-2 text-right whitespace-nowrap">
                <div className="flex items-center justify-end gap-1">
                    <button onClick={() => setViewCustomer(customer)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-md" title="View"><Eye size={14} /></button>
                    <button onClick={() => setEditCustomer(customer)} className="p-1.5 text-orange-600 hover:bg-orange-50 rounded-md" title="Edit"><Edit size={14} /></button>
                    {!isSalesPerson && (
                        <button onClick={() => handleToggleStatus(customer)}
                            className={`p-1.5 rounded-md ${customer.active ? 'text-green-600 hover:bg-green-50' : 'text-red-600 hover:bg-red-50'}`}
                            title={customer.active ? 'Deactivate' : 'Activate'}>
                            <Power size={14} />
                        </button>
                    )}
                </div>
            </td>
        </tr>
    );

    const renderCard = (customer) => (
        <CustomerCard 
            key={customer.id}
            customer={customer}
            onView={setViewCustomer}
            onEdit={setEditCustomer}
            onToggleStatus={handleToggleStatus}
            isSalesPerson={isSalesPerson}
            selected={selectedIds.has(customer.id)}
            onToggleSelect={() => handleToggleSelect(customer.id)}
        />
    );

    return (
        <div className="flex flex-col h-full bg-gray-50 overflow-hidden">
            <div className="p-4 bg-white border-b flex items-center justify-between">
                <h1 className="text-xl font-bold">Customers</h1>
                <div className="flex items-center gap-2">
                    <button
                        onClick={loadCustomers}
                        disabled={loading}
                        className="p-2 bg-white border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 hover:text-gray-900 transition-all flex items-center justify-center focus-visible:ring-2 focus-visible:ring-blue-500 focus:outline-none disabled:opacity-50"
                        title="Refresh customers"
                        aria-label="Refresh customer list"
                    >
                        <RefreshCw size={18} className={loading ? "animate-spin" : ""} />
                    </button>
                    {!isSalesPerson && (
                        <>
                            <div className="relative" ref={dropdownRef}>
                                <button onClick={() => setShowOptionsDropdown(!showOptionsDropdown)} className="px-4 py-2 border rounded-lg flex items-center gap-2 text-sm font-semibold hover:bg-gray-50 transition-all">
                                    <Settings size={18} />
                                    <span className="hidden sm:inline">Options</span>
                                    <ChevronDown size={16} />
                                </button>
                                {showOptionsDropdown && (
                                    <div className="absolute right-0 mt-2 w-56 bg-white border rounded-xl shadow-lg z-20 overflow-hidden text-left">
                                        <button onClick={() => handleExportAll()} className="w-full text-left px-4 py-3 text-sm hover:bg-gray-50 flex items-center gap-3">
                                            <FileDown size={16} /> Export All
                                        </button>
                                        <button onClick={handleDownloadTemplate} className="w-full text-left px-4 py-3 text-sm hover:bg-gray-50 flex items-center gap-3">
                                            <FileDown size={16} /> Download Template
                                        </button>
                                        <button onClick={() => fileInputRef.current?.click()} className="w-full text-left px-4 py-3 text-sm hover:bg-gray-50 flex items-center gap-3">
                                            <Plus size={16} /> Import from Excel
                                        </button>
                                        <input type="file" ref={fileInputRef} onChange={handleImportExcel} accept=".xlsx" className="hidden" />
                                    </div>
                                )}
                            </div>
                            <button
                                onClick={() => handleExportExcel()}
                                className="px-4 py-2 bg-white border border-blue-200 text-blue-600 rounded-lg hover:bg-blue-50 transition-all flex items-center gap-2 font-bold shadow-sm"
                            >
                                <FileDown size={18} />
                                <span className="hidden sm:inline">Export PDF</span>
                            </button>
                        </>
                    )}
                    {!isSalesPerson && (
                        <button
                            onClick={() => {
                                setMergeTarget(null);
                                setMergeDuplicates([]);
                                setShowMergeModal(true);
                            }}
                            className="px-4 py-2 bg-indigo-50 border border-indigo-200 text-indigo-700 hover:bg-indigo-100 rounded-lg transition-all flex items-center gap-2 font-bold text-sm shadow-sm"
                            title="Merge duplicate customer records"
                        >
                            <Merge size={18} />
                            <span className="hidden sm:inline">Merge Customers</span>
                        </button>
                    )}
                    <button 
                        className="px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all shadow-md hover:shadow-lg flex items-center gap-2 font-semibold text-sm" 
                        onClick={() => setEditCustomer({})}
                    >
                        <Plus size={18} />
                        <span className="hidden sm:inline">Add Customer</span>
                    </button>
                </div>
            </div>

            <div className="px-4 bg-white border-b flex items-center justify-between">
                <div className="flex gap-6">
                    <button 
                        className={`py-3 px-1 text-sm font-bold border-b-2 transition-all flex items-center gap-2 ${statusTab === 'ACTIVE' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-900'}`}
                        onClick={() => {
                            setStatusTab('ACTIVE');
                            setCurrentPage(1);
                        }}
                    >
                        <Users size={16} />
                        Active Customers
                    </button>
                    <button 
                        className={`py-3 px-1 text-sm font-bold border-b-2 transition-all flex items-center gap-2 ${statusTab === 'INACTIVE' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-900'}`}
                        onClick={() => {
                            setStatusTab('INACTIVE');
                            setCurrentPage(1);
                        }}
                    >
                        <Power size={16} className="rotate-180" />
                        Inactive Customers
                    </button>
                </div>
                
                {searchTerm && (
                    <button
                        onClick={() => {
                            setSearchTerm('');
                            setCurrentPage(1);
                        }}
                        className="flex items-center gap-1 text-[10px] text-blue-600 hover:text-blue-700 font-bold uppercase tracking-wider px-2"
                    >
                        <RefreshCw size={12} />
                        Reset Search
                    </button>
                )}
                
                {isSalesPerson && (
                    <div className="flex gap-4">
                        <button 
                            className={`py-3 px-1 text-xs font-bold border-b-2 transition-colors ${activeTab === 'assigned' ? 'border-blue-400 text-blue-500' : 'border-transparent text-gray-400 hover:text-gray-600'}`}
                            onClick={() => setActiveTab('assigned')}
                        >
                            Assigned to Me
                        </button>
                        <button 
                            className={`py-3 px-1 text-xs font-bold border-b-2 transition-colors ${activeTab === 'delegated' ? 'border-blue-400 text-blue-500' : 'border-transparent text-gray-400 hover:text-gray-600'}`}
                            onClick={() => setActiveTab('delegated')}
                        >
                            Delegated to Me
                        </button>
                    </div>
                )}

                <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-xl">
                    <button
                        onClick={() => setViewMode('grid')}
                        className={`p-1.5 rounded-lg transition-all ${viewMode === 'grid' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-400 hover:text-gray-600'}`}
                        title="Grid View"
                    >
                        <LayoutGrid size={16} />
                    </button>
                    <button
                        onClick={() => setViewMode('table')}
                        className={`p-1.5 rounded-lg transition-all ${viewMode === 'table' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-400 hover:text-gray-600'}`}
                        title="List View"
                    >
                        <List size={16} />
                    </button>
                </div>
            </div>

            <div className="flex-1 flex flex-col p-4 gap-4 min-h-0 overflow-hidden">

                <div className="bg-white rounded-xl border flex-1 overflow-hidden relative">
                    {loading && (
                        <div className="absolute inset-0 bg-white/40 backdrop-blur-[1px] flex items-center justify-center z-10 transition-all">
                            <div className="bg-white p-4 rounded-2xl shadow-xl border border-gray-100 flex flex-col items-center gap-3">
                                <LoadingSpinner size="lg" />
                                <span className="text-xs font-bold text-blue-600 animate-pulse">Fetching Data...</span>
                            </div>
                        </div>
                    )}
                    
                    <DataTable
                        data={displayCustomers}
                        columns={columns}
                        renderRow={renderRow}
                        renderCard={renderCard}
                        viewMode={viewMode}
                        searchPlaceholder="Search customers..."
                        serverSide={true}
                        totalElements={totalElements}
                        totalPages={totalPages}
                        currentPage={currentPage}
                        defaultItemsPerPage={pageSize}
                        onPageChange={setCurrentPage}
                        searchTerm={searchTerm}
                        onSearch={(val) => {
                            setSearchTerm(val);
                            setCurrentPage(1);
                        }}
                        onSort={(field, dir) => {
                            setSortField(field);
                            setSortDirection(dir);
                            setCurrentPage(1);
                        }}
                        onItemsPerPageChange={(size) => {
                            setPageSize(size);
                            setCurrentPage(1);
                        }}
                    />
                </div>
            </div>

            {(importing || importResult) && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-2xl p-8 text-center max-w-sm w-full shadow-2xl">
                        {importing ? (
                            <>
                                <RefreshCw className="animate-spin mx-auto text-blue-600 mb-4" size={32} />
                                <h3 className="text-lg font-bold">Importing...</h3>
                            </>
                        ) : (
                            <>
                                <Users className="mx-auto text-blue-600 mb-4" size={32} />
                                <h3 className="text-lg font-bold mb-4">Import Completed</h3>
                                <div className="grid grid-cols-2 gap-4 mb-6 text-sm">
                                    <div className="bg-green-50 p-2 rounded border border-green-100 font-bold text-green-700 text-center text-xs">Imported: {importResult?.imported || 0}</div>
                                    <div className="bg-yellow-50 p-2 rounded border border-yellow-100 font-bold text-yellow-700 text-center text-xs">Skipped: {importResult?.skipped || 0}</div>
                                </div>
                                <button onClick={() => setImportResult(null)} className="w-full py-2 bg-gray-900 text-white rounded font-bold">Close</button>
                            </>
                        )}
                    </div>
                </div>
            )}

            {viewCustomer && <CustomerViewModal customer={viewCustomer} onClose={() => setViewCustomer(null)} onEdit={(c) => { setViewCustomer(null); setEditCustomer(c); }} />}
            {editCustomer && <CustomerEditModal customer={editCustomer} role={role} onClose={() => setEditCustomer(null)} onSave={handleSaveCustomer} />}

            <MergeCustomerModal
                isOpen={showMergeModal}
                initialTarget={mergeTarget}
                initialDuplicates={mergeDuplicates}
                onClose={() => setShowMergeModal(false)}
                onSuccess={() => {
                    loadCustomers();
                    setSelectedIds(new Set());
                }}
            />

            {/* Floating Bulk Actions Bar */}
            {selectedIds.size > 0 && (
                <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-gray-900 text-white px-6 py-3.5 rounded-2xl shadow-2xl flex items-center gap-6 z-30 animate-slide-up border border-gray-800">
                    <div className="flex items-center gap-2">
                        <span className="bg-blue-600 text-white text-xs font-black px-2 py-0.5 rounded-lg">
                            {selectedIds.size}
                        </span>
                        <span className="text-xs font-bold text-gray-200">Selected</span>
                    </div>
                    <div className="h-4 w-px bg-gray-800" />
                    <div className="flex items-center gap-3">
                        {!isSalesPerson && (
                            <button
                                onClick={() => {
                                    const selectedCustomers = customers.filter(c => selectedIds.has(c.id || c.publicId));
                                    setMergeTarget(selectedCustomers[0] || null);
                                    setMergeDuplicates(selectedCustomers.slice(1));
                                    setShowMergeModal(true);
                                }}
                                className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-xl text-xs transition-all shadow-md flex items-center gap-1.5"
                            >
                                <Merge size={14} />
                                Merge Selected
                            </button>
                        )}
                        <button
                            onClick={() => setShowBulkReassignModal(true)}
                            className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl text-xs transition-all shadow-md flex items-center gap-1.5"
                        >
                            <Users size={14} />
                            Change Salesperson / Category
                        </button>
                        <button
                            onClick={() => setSelectedIds(new Set())}
                            className="px-3 py-1.5 bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white font-semibold rounded-xl text-xs transition-all"
                        >
                            Clear Selection
                        </button>
                    </div>
                </div>
            )}

            {/* Bulk Update Modal */}
            {showBulkReassignModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm animate-fade-in">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden p-6 space-y-4 animate-slide-up">
                        <div className="flex justify-between items-center pb-2 border-b border-gray-100">
                            <div>
                                <h3 className="text-base font-bold text-gray-900">Bulk Update Customers</h3>
                                <p className="text-xs text-gray-500 mt-0.5">Updating <span className="font-bold text-blue-600">{selectedIds.size}</span> selected customer(s)</p>
                            </div>
                            <button onClick={() => setShowBulkReassignModal(false)} className="text-gray-400 hover:text-gray-600">
                                <X size={20} />
                            </button>
                        </div>

                        <div className="space-y-4">
                            {/* Category Dropdown */}
                            <div className="space-y-1.5">
                                <label className="text-[11px] font-black text-gray-400 uppercase tracking-wider">Customer Category</label>
                                <select
                                    value={bulkCategory}
                                    onChange={(e) => setBulkCategory(e.target.value)}
                                    className="w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
                                >
                                    <option value="">-- Keep Current Category --</option>
                                    {lookupData.categories.map(cat => (
                                        <option key={cat.publicId} value={cat.publicId}>{cat.categoryName}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Primary Salesperson Dropdown */}
                            <div className="space-y-1.5">
                                <label className="text-[11px] font-black text-gray-400 uppercase tracking-wider">Primary Assigned Salesperson</label>
                                <select
                                    value={bulkPrimarySp}
                                    onChange={(e) => setBulkPrimarySp(e.target.value)}
                                    className="w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
                                >
                                    <option value="">-- Keep Current Primary Salesperson --</option>
                                    {lookupData.salesPersons.map(sp => (
                                        <option key={sp.publicId} value={sp.publicId}>{sp.fullName}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Delegated Salesperson Dropdown */}
                            <div className="space-y-1.5">
                                <label className="text-[11px] font-black text-gray-400 uppercase tracking-wider">Delegated Salesperson</label>
                                <select
                                    value={bulkDelegatedSp}
                                    onChange={(e) => setBulkDelegatedSp(e.target.value)}
                                    className="w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
                                >
                                    <option value="">-- Keep Current Delegated Salesperson --</option>
                                    {lookupData.salesPersons.map(sp => (
                                        <option key={sp.publicId} value={sp.publicId}>{sp.fullName}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="flex gap-3 pt-3">
                            <button
                                onClick={() => setShowBulkReassignModal(false)}
                                className="flex-1 py-2.5 border text-gray-500 font-semibold rounded-xl hover:bg-gray-50 text-xs transition-all"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleBulkReassignSubmit}
                                disabled={loading || (!bulkCategory && !bulkPrimarySp && !bulkDelegatedSp)}
                                className="flex-1 py-2.5 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 disabled:opacity-50 text-xs shadow-md transition-all flex items-center justify-center gap-1.5"
                            >
                                {loading ? 'Saving...' : 'Apply Changes'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CustomersView;

